
import React, { useState, useRef, useEffect } from 'react';
import Markdown from 'react-markdown';
import { getConstructionAdvice } from '../geminiService';

interface AIAdvisorProps {
  compact?: boolean;
}

const AIAdvisor: React.FC<AIAdvisorProps> = ({ compact }) => {
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    const result = await getConstructionAdvice(query);
    setResponse(result || "I couldn't generate advice right now.");
    setLoading(false);
  };

  useEffect(() => {
    if (response) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [response]);

  if (compact) {
    return (
      <div className="py-20 bg-blue-900 dark:bg-blue-950 text-white transition-colors">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h3 className="text-3xl font-extrabold mb-6">Have a Technical Question?</h3>
          <p className="text-blue-200 dark:text-blue-300 mb-8 max-w-xl mx-auto transition-colors">
            Our AI Advisor is trained on decades of infrastructure data to give you instant estimates and material recommendations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <input 
              type="text" 
              placeholder="e.g. Benefits of SMA asphalt for high traffic?" 
              className="bg-white/10 dark:bg-black/20 border border-white/20 rounded-lg px-6 py-4 flex-grow max-w-md outline-none focus:bg-white/20 transition text-white placeholder:text-blue-300"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button 
              onClick={handleSubmit}
              disabled={loading}
              className="bg-blue-500 hover:bg-blue-400 dark:bg-blue-600 dark:hover:bg-blue-500 disabled:opacity-50 px-8 py-4 rounded-lg font-bold transition shadow-lg"
            >
              {loading ? 'Thinking...' : 'Ask AI'}
            </button>
          </div>
          
          {response && (
            <div className="mt-8 bg-white/10 backdrop-blur-xl border border-white/20 p-8 rounded-2xl text-left max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex gap-4 mb-4">
                <div className="w-8 h-8 rounded-full bg-blue-400 flex items-center justify-center font-bold text-xs shrink-0">AI</div>
                <div className="text-blue-100 font-bold uppercase tracking-widest text-xs py-1.5">Sai SSK Advisor</div>
              </div>
              <div className="text-gray-100 leading-relaxed prose prose-invert max-w-none">
                <Markdown>{response}</Markdown>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 px-4 max-w-5xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-3xl border border-blue-100 dark:border-gray-700 shadow-2xl overflow-hidden min-h-[600px] flex flex-col transition-colors">
        {/* Chat Header */}
        <div className="bg-blue-800 dark:bg-blue-900 p-8 text-white transition-colors">
          <h2 className="text-2xl font-black mb-1">Sai SSK Infrastructure Advisor</h2>
          <p className="text-blue-200 dark:text-blue-300 opacity-90 transition-colors">Instant technical insights for road construction and materials.</p>
        </div>

        {/* Chat Body */}
        <div className="flex-grow p-8 overflow-y-auto space-y-6 bg-blue-50/30 dark:bg-gray-900/50 transition-colors">
          {!response && !loading && (
            <div className="h-full flex flex-col items-center justify-center text-center text-gray-400 dark:text-gray-500 space-y-4">
              <div className="w-16 h-16 bg-blue-100 dark:bg-gray-700 rounded-full flex items-center justify-center text-blue-800 dark:text-blue-300">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              </div>
              <div>
                <p className="font-bold text-gray-600 dark:text-gray-400 transition-colors">Ask about:</p>
                <ul className="text-sm space-y-1 transition-colors">
                  <li>• Asphalt vs Concrete costs</li>
                  <li>• Road durability factors</li>
                  <li>• Career path at Sai SSK</li>
                </ul>
              </div>
            </div>
          )}

          {loading && (
            <div className="flex gap-4 items-start animate-pulse">
              <div className="w-10 h-10 rounded-full bg-blue-200 dark:bg-gray-700"></div>
              <div className="bg-gray-200 dark:bg-gray-700 h-12 w-2/3 rounded-2xl"></div>
            </div>
          )}

          {response && (
            <div className="flex gap-4 items-start">
              <div className="w-10 h-10 rounded-full bg-blue-800 dark:bg-blue-600 flex items-center justify-center text-white shrink-0 shadow-lg">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-blue-100 dark:border-gray-700 shadow-sm text-gray-700 dark:text-gray-200 leading-relaxed transition-colors prose dark:prose-invert max-w-none">
                <Markdown>{response}</Markdown>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Chat Input */}
        <div className="p-8 border-t border-blue-50 dark:border-gray-700 bg-white dark:bg-gray-800 transition-colors">
          <form onSubmit={handleSubmit} className="flex gap-4">
            <input 
              type="text" 
              className="flex-grow bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-6 py-4 focus:ring-2 focus:ring-blue-500 outline-none transition text-gray-900 dark:text-white"
              placeholder="Type your infrastructure query here..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button 
              type="submit" 
              disabled={loading}
              className="bg-blue-800 dark:bg-blue-600 text-white px-8 py-4 rounded-xl font-bold hover:bg-blue-700 dark:hover:bg-blue-500 transition disabled:opacity-50 shadow-lg shadow-blue-100 dark:shadow-none"
            >
              Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AIAdvisor;
